import type * as Y from "yjs";
import type { SolvePhase, WidgetKind } from "@playbookai/types";
import { widgetsMap } from "@playbookai/collab-client";
import { getWidget } from "@playbookai/ui";
import {
  getSolveWidgetPlacement,
  type SolveCanvasWidgetKind,
  type SolveWidgetRect,
} from "@playbookai/widget-schemas";
import { readPhaseAreas } from "./phase-area-doc-shape.js";

/**
 * Local reservations, so two adds started before either persists cannot be
 * handed the same slot.
 *
 * Local ONLY: no canvas placeholder is broadcast, and a failed request releases
 * its reservation. This is not a distributed lock and the shared placement rule
 * is not a concurrency mechanism — the reservations are simply fed in as
 * occupied rectangles like any other.
 */
const pending = new WeakMap<Y.Doc, Set<SolveWidgetRect>>();

/**
 * Where the toolbar's next manual widget goes.
 *
 * The arithmetic — the area's content origin, the 40px gap, the downward sweep,
 * the phase-agnostic parking column and the widget's native size — all lives in
 * `getSolveWidgetPlacement` (`@playbookai/widget-schemas`), which Player AI's
 * server-side create calls with the same inputs. This function's whole job is to
 * turn the Yjs document into those inputs and to hold the reservation.
 */
export function reserveManualWidgetPosition(
  doc: Y.Doc,
  phase: SolvePhase | "any",
  kind: WidgetKind,
): { position: SolveWidgetRect; release: () => void } {
  const reservations = pending.get(doc) ?? new Set<SolveWidgetRect>();
  pending.set(doc, reservations);
  const occupied: SolveWidgetRect[] = [...reservations];
  widgetsMap(doc).forEach((map) => {
    const x = map.get("x");
    const y = map.get("y");
    if (!finite(x) || !finite(y)) return;
    // Actual rectangles, including widgets moved across an area boundary. A ref
    // with a missing or non-positive size falls back to its registered default,
    // then to a last-resort box — the salvage this path has always applied.
    const def = getWidget(map.get("kind") as WidgetKind);
    const w = map.get("w");
    const h = map.get("h");
    occupied.push({
      x,
      y,
      w: finite(w) && w > 0 ? w : (def?.defaultSize.w ?? 220),
      h: finite(h) && h > 0 ? h : (def?.defaultSize.h ?? 150),
    });
  });

  const position = getSolveWidgetPlacement({
    phase,
    widgetType: kind as SolveCanvasWidgetKind,
    phaseAreas: readPhaseAreas(doc),
    existingWidgets: occupied,
  });
  reservations.add(position);
  return {
    position,
    release: () => {
      reservations.delete(position);
      if (!reservations.size) pending.delete(doc);
    },
  };
}

function finite(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}
